import { z } from 'zod';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import Select from 'react-select';
import { toast } from 'sonner';
import SectionHeader from '@/components/FullPageCarousel/SectionHeader';
import {
  FormField,
  FormLabel,
  FormControl,
  FormItem,
  Form,
} from '@/components/ui/form';
import {
  IBitType,
  IBitUsedUnit,
  IToolData,
  IPostUploadData,
  IModel,
  ILaserOrEndmill,
} from '@/redux/post/post.d';
import ErrorMessage from '@/components/ErrorMessage';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useModelToolMappingForm } from '@/redux/post/usePostForm';
import Viewer from '@/components/ModelViewer';
import { modelToolMappingSchema } from '@/redux/post/post.schema';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import Loader from './Loader';

function ModelToolMapping({
  formData,
  pageErrors,
  setFormData,
}: {
  formData: IPostUploadData;
  pageErrors: { [key: string]: string };
  setFormData: Dispatch<SetStateAction<IPostUploadData>>;
}) {
  const modelToolMappingForm = useModelToolMappingForm(formData);
  const [currentModel, setCurrentModel] = useState<IModel | null>(null);
  const {
    formState: { errors },
  } = modelToolMappingForm;
  const [toolType, setToolType] = useState<ILaserOrEndmill>(
    ILaserOrEndmill.ENDMILL
  );

  const validateAndSaveToolData = async () => {
    try {
      const values = modelToolMappingForm.getValues();
      await modelToolMappingSchema.parseAsync(values);
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const { fieldErrors } = error.flatten();
        Object.entries(fieldErrors).forEach(([field, messages]) => {
          modelToolMappingForm.setError(
            field as keyof typeof modelToolMappingSchema._type,
            {
              message: messages?.[0],
            }
          );
        });
      }
      return false;
    }
  };

  const updateToolDataForModel = (
    prev: any,
    newLaserOrEndmill: ILaserOrEndmill,
    modelId: string,
    toolDataFormValues: any
  ) => {
    let updatedToolData =
      prev.toolData?.filter(
        (tool: any) =>
          tool.modelId !== modelId || tool.laserOrEndmill === newLaserOrEndmill
      ) || [];

    if (newLaserOrEndmill === ILaserOrEndmill.LASER) {
      const existingToolData = updatedToolData.find(
        (tool: any) =>
          tool.modelId === modelId &&
          tool.laserOrEndmill === ILaserOrEndmill.LASER
      );

      if (!existingToolData) {
        updatedToolData = [
          ...updatedToolData,
          {
            modelId,
            laserOrEndmill: newLaserOrEndmill,
            _id: Date.now().toString(),
          },
        ];
      } else {
        toast.warning('Only one tool data can be added for laser type');
      }
    } else {
      const newToolData: IToolData = {
        modelId,
        toolUnit: toolDataFormValues.toolUnit as IBitUsedUnit,
        toolUsed: toolDataFormValues.toolUsed as number,
        toolType: toolDataFormValues.toolType as IBitType,
        laserOrEndmill: newLaserOrEndmill,
        _id: Date.now().toString(),
      };

      updatedToolData = [...updatedToolData, newToolData];
    }

    return updatedToolData;
  };

  const addToolData = async () => {
    const isValid = await validateAndSaveToolData();
    if (!isValid) return;
    if (!currentModel) return;

    const newLaserOrEndmill = modelToolMappingForm.getValues(
      'laserOrEndmill'
    ) as ILaserOrEndmill;

    setFormData((prev) => {
      const updatedToolData = updateToolDataForModel(
        prev,
        newLaserOrEndmill,
        currentModel.id,
        modelToolMappingForm.getValues()
      );

      return { ...prev, toolData: updatedToolData };
    });

    setToolType(ILaserOrEndmill.ENDMILL);
    modelToolMappingForm.reset({
      toolUnit: IBitUsedUnit.INCHES,
      toolUsed: 0.015625,
      toolType: IBitType.UPCUT_ENDMILL,
      laserOrEndmill: ILaserOrEndmill.ENDMILL,
    });
  };

  const deleteToolData = (toolId: string) => {
    if (formData.toolData) {
      const updatedToolData = formData.toolData.filter(
        (tool) => tool._id !== toolId
      );
      setFormData((prev) => {
        return { ...prev, toolData: updatedToolData };
      });
    }
  };

  const handleModelChange = async (option: { value: string } | null) => {
    if (!option) return;
    const filteredModel = formData.models.find(
      (model) => model.id === option.value
    );
    if (!filteredModel) return;
    setCurrentModel(filteredModel);
    modelToolMappingForm.reset({
      toolUnit: IBitUsedUnit.INCHES,
      toolUsed: 0.015625,
      toolType: IBitType.UPCUT_ENDMILL,
      laserOrEndmill: ILaserOrEndmill.ENDMILL,
    });
    setToolType(ILaserOrEndmill.ENDMILL);
  };

  useEffect(() => {
    if (!formData.models) return;
    if (!formData.models.length) return;
    if (currentModel) return;

    setCurrentModel(formData.models[0]);
  }, [formData, currentModel]);

  if (!currentModel) {
    return <Loader isLoading />;
  }

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Map your Tools to your CNC models"
        description="Enter tools details for selected model"
      />
      <div className="h-[260px] border-dashed border-2 border-input rounded-lg text-center">
        <Viewer modelURL={currentModel.modelURL} backgroundColor="#FFFFFF" />
      </div>
      <Form {...modelToolMappingForm}>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="flex flex-col py-2 gap-4 flex-wrap">
            <div className="flex flex-col gap-2">
              <FormField
                name="selectedModel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Select your model and fill tool details
                    </FormLabel>
                    <FormControl>
                      <Select
                        options={formData.models.map((model) => {
                          return {
                            label: model.modelFile ? model.modelFile.name : '',
                            value: model.id,
                          };
                        })}
                        onChange={handleModelChange}
                        value={
                          currentModel?.modelFile
                            ? {
                                label: currentModel.modelFile.name,
                                value: currentModel.id,
                              }
                            : null
                        }
                        isSearchable
                        name={field.name}
                      />
                    </FormControl>
                    <ErrorMessage error={pageErrors.selectedModel || ''} />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <FormField
                control={modelToolMappingForm.control}
                name="laserOrEndmill"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Laser or Endmill</FormLabel>
                    <FormControl>
                      <Select
                        options={Object.values(ILaserOrEndmill).map(
                          (option) => ({
                            label: option,
                            value: option,
                          })
                        )}
                        onChange={(option) => {
                          if (option) {
                            field.onChange(option.value);
                            setToolType(option.value as ILaserOrEndmill);
                          }
                        }}
                        value={{
                          label: field.value,
                          value: field.value,
                        }}
                        isSearchable
                        name={field.name}
                      />
                    </FormControl>
                    <ErrorMessage
                      error={errors.laserOrEndmill?.message || ''}
                    />
                  </FormItem>
                )}
              />
            </div>
            {toolType === ILaserOrEndmill.ENDMILL ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <FormField
                      control={modelToolMappingForm.control}
                      name="toolUnit"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tool Used Units</FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className="flex gap-4"
                            >
                              {Object.values(IBitUsedUnit).map((option) => (
                                <div
                                  key={option}
                                  className="flex items-center space-x-2 w-full border border-[#D6D6D6] h-[40px] p-2 rounded-sm cursor-pointer"
                                >
                                  <RadioGroupItem
                                    value={option}
                                    id={`${option}-bit-used-unit`}
                                  />
                                  <label
                                    htmlFor={`${option}-bit-used-unit`}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1"
                                  >
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </RadioGroup>
                          </FormControl>
                          <ErrorMessage
                            error={(errors as any).toolUnit?.message || ''}
                          />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <FormField
                      control={modelToolMappingForm.control}
                      name="toolUsed"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Tool Used in{' '}
                            {modelToolMappingForm.getValues('toolUnit')}
                          </FormLabel>
                          <FormControl>
                            <input
                              type="number"
                              {...field}
                              onChange={(e) => field.onChange(e.target.value)}
                              onBlur={(e) =>
                                field.onChange(parseFloat(e.target.value))
                              }
                              step="any"
                              className="w-full p-2 border rounded"
                            />
                          </FormControl>
                          <ErrorMessage
                            error={(errors as any).toolUsed?.message || ''}
                          />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <FormField
                    control={modelToolMappingForm.control}
                    name="toolType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tool Bit Type</FormLabel>
                        <FormControl>
                          <Select
                            options={Object.values(IBitType).map((option) => ({
                              label: option,
                              value: option,
                            }))}
                            onChange={(option) => {
                              if (option) {
                                field.onChange(option.value);
                              }
                            }}
                            value={{
                              label: field.value,
                              value: field.value,
                            }}
                            isSearchable
                            name={field.name}
                          />
                        </FormControl>
                        <ErrorMessage
                          error={(errors as any).toolType?.message || ''}
                        />
                      </FormItem>
                    )}
                  />
                </div>
              </>
            ) : (
              <div />
            )}

            <Button
              type="button"
              onClick={addToolData}
              className="w-full bg-primary text-white"
            >
              Add Tool
            </Button>
          </div>
        </form>
      </Form>

      <div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Tool Type</TableHead>
              <TableHead>Tool Bit Type</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.isArray(formData.toolData) &&
              (formData.toolData as IToolData[])
                .filter((tool) => tool.modelId === currentModel?.id)
                .map((tool, index) => (
                  <TableRow key={tool._id}>
                    <TableCell>{index + 1}</TableCell>
                    {tool.laserOrEndmill === ILaserOrEndmill.ENDMILL ? (
                      <>
                        <TableCell>
                          {tool.toolUsed} {tool.toolUnit}
                        </TableCell>
                        <TableCell>{tool.toolType}</TableCell>
                        <TableCell>{tool.laserOrEndmill}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>Nill</TableCell>
                        <TableCell>Nill</TableCell>
                        <TableCell>{tool.laserOrEndmill}</TableCell>
                      </>
                    )}
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteToolData(tool._id || '')}
                        className="text-destructive hover:text-destructive/90"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default ModelToolMapping;
